import React, { useEffect, useState } from "react";
import configVars from "../../config";
import { useCookies } from "react-cookie";
import jwt_decode from "jwt-decode";
import Alert from "../Alert";
import validator from "validator";
import { useTranslation } from "react-i18next";
import { setWithExpiry } from "../../common";
import ProfilePic from "./ProfilePic";
import UserMeta from "./UserMeta";
import { useSelector } from "react-redux";

const UserSettings = () => {
  const [cookies] = useCookies(["sustainyou_user_jwt"]);
  const [saving, setSaving] = useState(false);
  const [displayAlert, setDisplayAlert] = useState({ type: "", msg: "" });

  const [translation] = useTranslation("translations");

  const [editFirstName, setEditFirstName] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [firstNameInit, setFirstNameInit] = useState("");

  const [editLastName, setEditLastName] = useState(false);
  const [lastName, setLastName] = useState("");
  const [lastNameInit, setLastNameInit] = useState("");

  const [editUserEmail, setEditUserEmail] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userEmailInit, setUserEmailInit] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [editConfirmEmail, setEditConfirmEmail] = useState(false);

  const [editUserPass, setEditUserPass] = useState(false);
  const [editCurrentPass, setEditCurrentPass] = useState(false);
  const [userPass, setUserPass] = useState("");
  const [currentPass, setCurrentPass] = useState("");

  const allUserData = useSelector((state) => state?.userdata.allUserData);

  const spinner = (
    <div className="spinner-grow spinner-grow-sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  const rotatingSpinner = (
    <div className="spinner-border spinner-border-sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  useEffect(() => {
    if (allUserData?.first_name) {
      setFirstName(allUserData?.first_name);
      setFirstNameInit(allUserData?.first_name);
    }

    if (allUserData?.last_name) {
      setLastName(allUserData?.last_name);
      setLastNameInit(allUserData?.last_name);
    }

    if (allUserData?.email) {
      setUserEmail(allUserData?.email);
      setUserEmailInit(allUserData?.email);
    }
  }, [allUserData]);

  const handleUserUpdate = async (item, value) => {
    if (!item && !value) return;

    if (!cookies.sustainyou_user_jwt) return;

    if (item === "user_email") {
      if (!validator.isEmail(value)) {
        setDisplayAlert({
          type: "danger",
          msg: translation("enter_valid_email"),
        });
        setTimeout(() => {
          setDisplayAlert({ type: "", msg: "" });
        }, 5000);

        return;
      }

      if (userEmail !== confirmEmail) {
        setDisplayAlert({
          type: "danger",
          msg: translation("email_not_match"),
        });
        setTimeout(() => {
          setDisplayAlert({ type: "", msg: "" });
        }, 5000);

        return;
      }
    }

    if (item === "first_name" || item === "last_name") {
      if (value.length < 2) {
        setDisplayAlert({
          type: "danger",
          msg: translation("name_char_limit"),
        });
        setTimeout(() => {
          setDisplayAlert({ type: "", msg: "" });
        }, 5000);

        return;
      }
    }

    if (item === "user_pass") {
      if (value.length < 8) {
        setDisplayAlert({
          type: "danger",
          msg: translation("pswd_char_limit"),
        });
        setTimeout(() => {
          setDisplayAlert({ type: "", msg: "" });
        }, 5000);

        return;
      }

      if (
        !validator.isStrongPassword(value, {
          minLength: 8,
          minLowercase: 1,
          minUppercase: 1,
          minNumbers: 1,
          minSymbols: 1,
        })
      ) {
        setDisplayAlert({ type: "danger", msg: translation("pswd_limit") });
        setTimeout(() => {
          setDisplayAlert({ type: "", msg: "" });
        }, 5000);

        return;
      }
    }

    setSaving(true);

    const jwtDecoded = jwt_decode(cookies.sustainyou_user_jwt);

    const fetchUrl = `${
      configVars.apiUrl
    }sustainchange/v1/updateuser/?update_item=${item}&update_value=${encodeURIComponent(
      value
    )}&id=${jwtDecoded.id}`;

    const response = await fetch(fetchUrl, {
      method: "GET",
      headers: {
        Authorization: `Basic ${configVars.auth}`,
      },
    });
    const updateResponse = await response.json();

    if (updateResponse) {
      if (updateResponse.success) {
        if (item !== "check_pass") {
          setDisplayAlert({
            type: "success",
            msg: translation("settings_saved"),
          });
        }

        if (item === "first_name") {
          setEditFirstName(false);
          setFirstNameInit(firstName);
        }

        if (item === "last_name") {
          setEditLastName(false);
          setLastNameInit(lastName);
        }

        if (item === "user_email") {
          setEditUserEmail(false);
          setUserEmailInit(userEmail);
        }

        if (item === "user_pass") {
          setEditUserPass(false);
        }

        if (item === "check_pass") {
          setEditUserPass(true);
          setEditCurrentPass(false);
        }

        setWithExpiry("sy_user_first_name", firstName);
        localStorage.removeItem("user_data_expire");
      } else {
        setDisplayAlert({
          type: "danger",
          msg: translation("something_wrong"),
        });
      }

      setTimeout(() => {
        setDisplayAlert({ type: "", msg: "" });
      }, 5000);
    }

    //console.log( updateResponse );

    setSaving(false);
  };

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-lg-9">
        <div className="user-settings-alerts mx-auto mt-6">
          {displayAlert.type.length > 0 && (
            <Alert type={displayAlert.type} msg={displayAlert.msg} />
          )}
        </div>
        <div className="user-settings-wrap my-5">
          <div className="user-settings-inner mx-auto border border-light-gray">
            <div className="user-settings-row user-profile-pic-wrap position-relative pt-5">
              <ProfilePic />
            </div>
            <div className="user-settings-row px-4 py-3 border-bottom border-light-gray">
              <div className="user-settings-row-inner px-2 py-1 d-flex align-items-center">
                <div className="user-settings-data flex-grow-1">
                  <span className="setting-label d-inline-block w-100 placeholder-text">
                    {translation("first_name")}
                  </span>
                  {!editFirstName && (
                    <span className="setting-value opacity-half d-inline-block w-100">
                      {firstName}
                    </span>
                  )}
                </div>
                {!editFirstName && (
                  <div className="user-settings-icon">
                    <button
                      type="button"
                      className="text-white py-1 px-2 bg-secondary btn btn-link"
                      onClick={() => setEditFirstName(true)}
                    >
                      <i className="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>

              {editFirstName && (
                <>
                  <div className="input-group px-2 py-1">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={translation("your_first_name")}
                      value={firstName || ""}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    <div className="input-group-append">
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() =>
                          handleUserUpdate("first_name", firstName)
                        }
                      >
                        <span className="me-2">
                          {translation("buttons.save")}
                        </span>
                        {saving && rotatingSpinner}
                      </button>
                    </div>
                  </div>
                  <div className="text-right d-inline-block w-100">
                    <button
                      type="button"
                      className="text-secondary float-end btn btn-link pb-0"
                      onClick={() => {
                        setEditFirstName(false);
                        setFirstName(firstNameInit);
                      }}
                    >
                      {translation("buttons.cancel")}
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="user-settings-row px-4 py-3 border-bottom border-light-gray">
              <div className="user-settings-row-inner px-2 py-1 d-flex align-items-center">
                <div className="user-settings-data flex-grow-1">
                  <span className="setting-label d-inline-block w-100 placeholder-text">
                    {translation("last_name")}
                  </span>
                  {!editLastName && (
                    <span className="setting-value opacity-half d-inline-block w-100">
                      {lastName}
                    </span>
                  )}
                </div>
                {!editLastName && (
                  <div className="user-settings-icon">
                    <button
                      type="button"
                      className="text-white py-1 px-2 bg-secondary btn btn-link"
                      onClick={() => {
                        setEditLastName(true);
                      }}
                    >
                      <i className="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
              {editLastName && (
                <>
                  <div className="input-group px-2 py-1">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={translation("your_last_name")}
                      value={lastName || ""}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    <div className="input-group-append">
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => handleUserUpdate("last_name", lastName)}
                      >
                        <span className="me-2">
                          {translation("buttons.save")}
                        </span>
                        {saving && rotatingSpinner}
                      </button>
                    </div>
                  </div>
                  <div className="text-right d-inline-block w-100">
                    <button
                      type="button"
                      className="text-secondary float-end btn btn-link pb-0"
                      onClick={() => {
                        setEditLastName(false);
                        setLastName(lastNameInit);
                      }}
                    >
                      {translation("buttons.cancel")}
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="user-settings-row px-4 py-3 border-bottom border-light-gray">
              <div className="user-settings-row-inner px-2 py-1 d-flex align-items-center">
                <div className="user-settings-data flex-grow-1">
                  <span className="setting-label d-inline-block w-100 placeholder-text">
                    {translation("email_address")}
                  </span>
                  {!editUserEmail && (
                    <span className="setting-value opacity-half d-inline-block w-100">
                      {userEmail || spinner}
                    </span>
                  )}
                </div>
                {!editUserEmail && (
                  <div className="user-settings-icon">
                    <button
                      type="button"
                      className="text-white bg-secondary py-1 px-2 btn btn-link"
                      onClick={() => {
                        setEditUserEmail(true);
                      }}
                    >
                      <i className="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
              {editUserEmail && (
                <>
                  {!editConfirmEmail && (
                    <div className="input-group px-2 py-1">
                      <input
                        type="email"
                        className="form-control"
                        placeholder={translation("your_email")}
                        value={userEmail || ""}
                        onChange={(e) => setUserEmail(e.target.value)}
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => setEditConfirmEmail(true)}
                        >
                          {translation("next")}
                        </button>
                      </div>
                    </div>
                  )}

                  {editConfirmEmail && (
                    <div className="input-group px-2 py-1">
                      <input
                        type="email"
                        className="form-control"
                        placeholder={translation("confirm_email")}
                        value={confirmEmail || ""}
                        onChange={(e) => setConfirmEmail(e.target.value)}
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() =>
                            handleUserUpdate("user_email", userEmail)
                          }
                        >
                          <span className="me-2">
                            {translation("buttons.save")}
                          </span>
                          {saving && rotatingSpinner}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="text-right d-inline-block w-100">
                    <button
                      type="button"
                      className="text-secondary float-end btn btn-link pb-0"
                      onClick={() => {
                        setEditUserEmail(false);
                        setUserEmail(userEmailInit);
                        setEditConfirmEmail(false);
                      }}
                    >
                      {translation("buttons.cancel")}
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="user-settings-row px-4 py-3">
              <div className="user-settings-row-inner px-2 py-1 d-flex align-items-center">
                <div className="user-settings-data flex-grow-1">
                  <span className="setting-label d-inline-block w-100 placeholder-text">
                    {translation("password")}
                  </span>
                  {!editUserPass && !editCurrentPass && (
                    <span className="setting-value opacity-half d-inline-block w-100">
                      *******************
                    </span>
                  )}
                </div>
                {!editUserPass && !editCurrentPass && (
                  <div className="user-settings-icon">
                    <button
                      type="button"
                      className="text-white bg-secondary py-1 px-2 btn btn-link"
                      onClick={() => setEditCurrentPass(true)}
                    >
                      <i className="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
              {(editUserPass || editCurrentPass) && (
                <>
                  {editCurrentPass && (
                    <div className="input-group px-2 py-1">
                      <input
                        type="password"
                        className="form-control"
                        placeholder={translation("current_pswd")}
                        value={currentPass || ""}
                        onChange={(e) => setCurrentPass(e.target.value)}
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() =>
                            handleUserUpdate("check_pass", currentPass)
                          }
                        >
                          <span className="me-2">{translation("next")}</span>
                          {saving && rotatingSpinner}
                        </button>
                      </div>
                    </div>
                  )}

                  {editUserPass && (
                    <div className="input-group px-2 py-1">
                      <input
                        type="password"
                        className="form-control"
                        placeholder={translation("new_pswd")}
                        value={userPass || ""}
                        onChange={(e) => setUserPass(e.target.value)}
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() =>
                            handleUserUpdate("user_pass", userPass)
                          }
                        >
                          <span className="me-2">
                            {translation("buttons.save")}
                          </span>
                          {saving && rotatingSpinner}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="text-right d-inline-block w-100">
                    <button
                      type="button"
                      className="text-secondary float-end btn btn-link pb-0"
                      onClick={() => {
                        setEditUserPass(false);
                        setEditCurrentPass(false);
                      }}
                    >
                      {translation("buttons.cancel")}
                    </button>
                  </div>
                </>
              )}
            </div>

            <UserMeta
              options={["Kvinna", "Man", "Annat"]}
              optionkey="user_sex"
              label="Kön:"
              key="user_sex"
            />
            <UserMeta
              options={["Under 30", "30-50 år", "Över 50 år"]}
              optionkey="age_group"
              label="Ålder:"
              key="age_group"
            />
            <UserMeta
              options={["Ensam", "Med partner"]}
              optionkey="living_with"
              label="Jag bor:"
            />
            <UserMeta
              options={[
                "Hemmavarande barn under 12 år",
                "Hemmavarande barn över 12 år",
                "Hemmavarande barn under och över 12 år",
                "Inga hemmavarande barn",
              ]}
              optionkey="has_children"
              label="Jag har:"
            />
            <UserMeta
              options={[
                "I någon av storstadsregionerna Stockholm, Göteborg eller Malmö",
                "Annan plats",
              ]}
              optionkey="living_in"
              label="Boendeort:"
            />
            <UserMeta
              options={["Medarbetare", "Chef"]}
              optionkey="user_work"
              label="Jag är:"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
